import { lazy, Suspense, useState } from "react"
import PropTypes from "prop-types"
import classnames from "classnames"
import SectionHeader from "partners/components/static_applet_composer/section_header"
import InlineErrors from "shared/components/inline_errors"
import Modal from "shared/components/modal"
import { appletConfigShape } from "./applet_configuration"
import Mustache from "mustache"

import styles from "./connection_scripting_editor.module.scss"

const MonacoEditor = lazy(() => import("shared/components/monaco_editor/index.jsx"))

export default function ConnectionScriptingEditor({
  configurations,
  contextData,
  contextTemplate,
  errors,
  scriptCode,
  onChange,
  runtimeScriptDocUrl,
}) {
  const [showIngredientModal, updateModalVisibility] = useState(false)
  const triggersSelected = extractStep(configurations, "triggers")
  const queriesSelected = extractStep(configurations, "queries")
  const actionsSelected = extractStep(configurations, "actions")

  return (
    <div style={{ width: "100%" }} className="connection-scripting-container">
      <div className="fieldset composer-fieldset">
        <div className="partners-tanda-composer field">
          <SectionHeader
            title="Script"
            actions={
              <button
                type="button"
                className={styles.reference}
                onClick={() => updateModalVisibility(!showIngredientModal)}
              >
                Open reference
              </button>
            }
          />

          <InlineErrors errors={errors} />

          <div className="partners-filter-code-container">
            <Suspense fallback="Loading...">
              <MonacoEditor
                defaultValue={scriptCode}
                onChange={onChange}
                typescriptContext={Mustache.render(contextTemplate, contextData)}
              />
            </Suspense>
          </div>
        </div>
      </div>

      <Modal
        closeListener={() => updateModalVisibility(false)}
        moveCloseXToHeader
        leftJustifyHeader
        headerText="Runtime script reference"
        show={showIngredientModal}
      >
        <div className={styles["documentation-reference"]}>
          <a
            href={runtimeScriptDocUrl}
            target="_blank"
            title="Runtime script documentation"
            rel="noreferrer"
          >
            Runtime script documentation
          </a>
        </div>

        <div className="platform-filter-code-api-list">
          {triggersSelected.length > 0 &&
            triggersSelected.map(trigger => {
              const key = trigger.full_module_name
              return (
                <div key={key}>
                  <h4>
                    Trigger data (<i>{key}</i>)
                  </h4>
                  <ul>
                    {trigger.ingredients.map(ingredient => (
                      <li key={ingredient.normalized_name}>
                        {`Trigger.ingredients.${ingredient.slug}`}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          {queriesSelected.length > 0 && (
            <div>
              <h4>Queries</h4>
              <ul>
                {queriesSelected.map(query => {
                  const key = query.full_module_name
                  return <li key={key}>performQuery(&quot;{key}&quot;)</li>
                })}
              </ul>
            </div>
          )}
          {actionsSelected.length > 0 && (
            <div>
              <h4>Actions</h4>
              <ul>
                {actionsSelected.map(action => {
                  const key = action.full_module_name
                  return (
                    <li key={key}>
                      runAction(&quot;{key}&quot;, &#123;&#125;)
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
          <div>
            <h4>Other</h4>
            <ul>
              <li>Meta.currentUserTime</li>
              <li>Meta.triggerTime</li>
              <li>Meta.previousTriggerTime</li>
            </ul>
          </div>
        </div>
      </Modal>
    </div>
  )
}

function extractStep(configurations, step) {
  return configurations.filter(c => !!c[step]).flatMap(c => c[step])
}

ConnectionScriptingEditor.propTypes = {
  configurations: PropTypes.arrayOf(appletConfigShape),
  contextTemplate: PropTypes.string.isRequired,
  contextData: PropTypes.object.isRequired,
  runtimeScriptDocUrl: PropTypes.string.isRequired,
  scriptCode: PropTypes.string,
  onChange: PropTypes.func,
  errors: InlineErrors.propTypes.errors,
}
